import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

/**
 * Possible profile roles. 'user' is the default any new signup gets
 * (intended for the mobile player app, not the web). 'venue_admin' is
 * assigned by the approve-venue Edge Function. 'admin' is set manually
 * via SQL for staff accounts.
 */
export type ProfileRole = "user" | "admin" | "venue_admin";

export interface Profile {
  id: string;
  role: ProfileRole;
  display_name: string | null;
  venue_application_id: string | null;
}

interface AuthState {
  /** Still fetching the session / profile on first mount. */
  loading: boolean;
  /** Raw Supabase session, null when signed out. */
  session: Session | null;
  /** Raw Supabase user, null when signed out. */
  user: User | null;
  /** Profile row joined on the authenticated user's id. */
  profile: Profile | null;
  /** Convenience flags derived from profile.role. */
  isAdmin: boolean;
  isVenueAdmin: boolean;
  /** Sign out and clear session. */
  signOut: () => Promise<void>;
  /** Refetch the profile row (e.g. after a role change). */
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * AuthProvider wraps the app and keeps a single Supabase session in sync
 * with React state. Pair with <RequireAuth /> to gate routes.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);

  // Keep the session in sync with Supabase's listener. This fires on
  // sign-in, sign-out, token refresh, and across-tab changes.
  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      // Profile load happens in the effect below; don't race it here.
    });

    const { data } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => {
      cancelled = true;
      data.subscription.unsubscribe();
    };
  }, []);

  // Load the profile row whenever the signed-in user changes.
  useEffect(() => {
    let cancelled = false;
    const userId = session?.user?.id;
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    supabase
      .from("profiles")
      .select("id, role, display_name, venue_application_id")
      .eq("id", userId)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          // User might not have a profile row yet (brand-new invite).
          // That's fine - we surface it as null and the UI can handle it.
          setProfile(null);
        } else {
          setProfile(data as Profile);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const value = useMemo<AuthState>(() => {
    const role = profile?.role ?? null;
    return {
      loading,
      session,
      user: session?.user ?? null,
      profile,
      isAdmin: role === "admin",
      isVenueAdmin: role === "venue_admin",
      signOut: async () => {
        await supabase.auth.signOut();
      },
      refreshProfile: async () => {
        if (!session?.user?.id) return;
        const { data } = await supabase
          .from("profiles")
          .select("id, role, display_name, venue_application_id")
          .eq("id", session.user.id)
          .single();
        if (data) setProfile(data as Profile);
      },
    };
  }, [loading, session, profile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access the current auth state. */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider />");
  return ctx;
}
